import { createSignal, createResource, For, Show, onCleanup } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import Text from '../../components/Text';
import { EmptyState } from '../../components/EmptyState';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDateTime } from '../../services/dateFormat';
import BotScoped from '../../components/BotScoped';
import type { SurveillanceSessionDto } from '../../api/generated/schemas/surveillanceSessionDto';
import { getSurveillanceDownloadSessionUrl } from '../../api/generated/surveillance';
import styles from './Surveillance.module.css';

export default function SurveillancePage() {
    return <BotScoped><SurveillanceInner /></BotScoped>;
}

function SurveillanceInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId: activeBotId } = useActiveBot();
    const botId = () => activeBotId() ?? '';

    const [taskStatus, { refetch: refetchStatus }] = createResource(
        botId,
        async (id) => {
            if (!id) return null;
            const res = await api.botTask.getTaskStatus(id);
            if (!isOk(res)) return null;
            return res.data.value;
        }
    );

    const isRecording = () => {
        const tree = taskStatus()?.tree;
        return tree?.pluginName === 'Surveillance' && tree?.taskName === 'StartRecording' && tree?.status === 'Running';
    };

    const [sessions, { refetch: refetchSessions }] = createResource(
        botId,
        async (id) => {
            if (!id) return [];
            const res = await api.surveillance.getSessions(id);
            if (!isOk(res)) return [];
            return res.data.value?.sessions ?? [];
        }
    );

    const interval = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        refetchStatus();
        refetchSessions();
    }, 3000);
    onCleanup(() => clearInterval(interval));

    const [starting, setStarting] = createSignal(false);
    const [stopping, setStopping] = createSignal(false);

    async function startRecording() {
        setStarting(true);
        try {
            const res = await api.botTask.runTask(botId(), { plugin: 'Surveillance', task: 'StartRecording' });
            if (isOk(res)) {
                refetchStatus();
                refetchSessions();
            } else {
                toast?.error(t('SURV_START_ERROR'));
            }
        } finally {
            setStarting(false);
        }
    }

    async function stopRecording() {
        setStopping(true);
        try {
            const res = await api.botTask.cancelTask(botId());
            if (isOk(res)) {
                refetchStatus();
                setTimeout(refetchSessions, 1500);
            } else {
                toast?.error(t('SURV_STOP_ERROR'));
            }
        } finally {
            setStopping(false);
        }
    }

    async function deleteSession(session: SurveillanceSessionDto) {
        if (!await showConfirm(t('SURV_CONFIRM_DELETE_SESSION'), true)) return;
        const res = await api.surveillance.deleteSession(botId(), session.id);
        if (isOk(res)) {
            toast?.success(t('SURV_SESSION_DELETED'));
            refetchSessions();
        } else {
            toast?.error(t('SURV_SESSION_DELETE_ERROR'));
        }
    }

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1 style={{ margin: 0 }}><Text val="SURV_TITLE" /></h1>
                </div>
                <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                    <Show when={isRecording()}>
                        <span class={styles.recordingBadge}>
                            <i class="fa-solid fa-circle" style={{ color: 'var(--error-500)', 'font-size': '0.5rem' }} />
                            {' '}<Text val="SURV_RECORDING" />
                        </span>
                    </Show>
                    <Show
                        when={isRecording()}
                        fallback={
                            <button
                                class="btn btn-primary btn-sm"
                                disabled={starting() || taskStatus.state === 'pending'}
                                onClick={startRecording}
                            >
                                <i class="fa-solid fa-circle-dot" />
                                {' '}<Text val="SURV_START" />
                            </button>
                        }
                    >
                        <button
                            class="btn btn-danger btn-sm"
                            disabled={stopping()}
                            onClick={stopRecording}
                        >
                            <i class="fa-solid fa-stop" />
                            {' '}<Text val="SURV_STOP" />
                        </button>
                    </Show>
                </div>
            </div>

            <Show when={sessions.state !== 'pending'} fallback={<SkeletonRows />}>
                <Show
                    when={(sessions() ?? []).length > 0}
                    fallback={
                        <EmptyState
                            icon="fa-eye-slash"
                            title={t('SURV_EMPTY')}
                        />
                    }
                >
                    <div class={styles.list}>
                        <For each={sessions()}>
                            {(session) => (
                                <SessionRow
                                    session={session}
                                    botId={botId()}
                                    onDelete={() => deleteSession(session)}
                                />
                            )}
                        </For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}

function SessionRow(props: { session: SurveillanceSessionDto; botId: string; onDelete: () => void }) {
    const { t } = useTranslation();
    const s = () => props.session;
    const running = () => !s().endedAt;
    const downloadUrl = () => getSurveillanceDownloadSessionUrl(props.botId, s().id);

    return (
        <div class={`fcard ${styles.sessionRow}`}>
            <div class={styles.sessionIcon}>
                <i
                    class={running() ? 'fa-solid fa-circle-dot' : 'fa-solid fa-circle-check'}
                    style={{ color: running() ? 'var(--error-500)' : 'var(--success-500)' }}
                />
            </div>
            <div class={styles.sessionBody}>
                <div class="h" style={{ gap: 'var(--spacing-md)', 'align-items': 'center', 'flex-wrap': 'wrap' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="SURV_SESSION_STARTED" />
                        {': '}
                        <span style={{ color: 'var(--dark-text-primary)', 'font-family': 'var(--font-family-mono)' }}>
                            {formatDateTime(s().startedAt)}
                        </span>
                    </span>
                    <Show when={!running()}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="SURV_SESSION_ENDED" />
                            {': '}
                            <span style={{ color: 'var(--dark-text-primary)', 'font-family': 'var(--font-family-mono)' }}>
                                {formatDateTime(s().endedAt!)}
                            </span>
                        </span>
                    </Show>
                    <Show when={running()}>
                        <span class={styles.runningBadge}><Text val="SURV_SESSION_RUNNING" /></span>
                    </Show>
                </div>
                <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                    {t('SURV_SESSION_EVENTS', s().eventCount)}
                </span>
            </div>
            <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-shrink': '0' }}>
                <a
                    href={downloadUrl()}
                    download=""
                    class="btn btn-secondary btn-sm"
                >
                    <i class="fa-solid fa-download" />
                    {' '}<Text val="SURV_DOWNLOAD" />
                </a>
                <button class="btn btn-danger btn-sm" onClick={props.onDelete}>
                    <i class="fa-solid fa-trash" />
                </button>
            </div>
        </div>
    );
}

function SkeletonRows() {
    return (
        <div class={styles.list}>
            <For each={Array(4).fill(0)}>
                {() => <div class={styles.skeleton} />}
            </For>
        </div>
    );
}
